import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../api/endpoints";
import type {
  AlertListQuery,
  AlertRule,
  DeviceEventQuery,
  DeviceListQuery,
  DevicePatchQuery,
  EnrollmentTokenRequest,
  MetricName,
  SnmpTargetCreate,
  UserCreateRequest,
  UserUpdateRequest,
} from "../api/types";
import { averageSeries, rangeIntervalSeconds, rangeWindow, type RangeKey } from "./metrics";
import type { ChartPoint } from "../components/charts/TimeSeriesChart";

/* ------------------------------------------------------------------ */
/* Cihazlar                                                            */
/* ------------------------------------------------------------------ */

/**
 * Cihazın online/offline durumu sunucuda `last_seen` üzerinden türetilir;
 * veritabanındaki `status` kolonu anlık değildir. Panel bu yüzden listeyi
 * periyodik olarak yenilemek zorunda — aksi halde cihaz kapansa bile sayfa
 * yenilenene kadar "online" görünür.
 *
 * 30 sn: offline eşiği 2 dk olduğundan geçişler en geç ~2.5 dk içinde görünür,
 * paylaşılan rate limit'e etkisi ise ihmal edilebilir (2 istek/30 sn).
 */
export const DEVICE_STATUS_REFRESH_MS = 30_000;

export function useDevices(query: DeviceListQuery) {
  return useQuery({
    queryKey: ["devices", query],
    queryFn: () => api.devices.list(query),
    placeholderData: keepPreviousData,
    refetchInterval: DEVICE_STATUS_REFRESH_MS,
  });
}

export function useDevice(id: string | undefined) {
  return useQuery({
    queryKey: ["device", id],
    queryFn: () => api.devices.get(id as string),
    enabled: Boolean(id),
    refetchInterval: DEVICE_STATUS_REFRESH_MS,
  });
}

/**
 * Cihaz kaydını siler. Yeniden enrollment yapılmış makinelerin eski/çevrimdışı
 * kayıtlarını temizlemek için kullanılır; sunucu ilişkili envanter, patch, olay
 * ve uyarı kayıtlarını da cascade ile kaldırır.
 */
export function useDeleteDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.devices.remove(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["device", id] });
      queryClient.removeQueries({ queryKey: ["device-metrics", id] });
      queryClient.removeQueries({ queryKey: ["device-inventory", id] });
      queryClient.removeQueries({ queryKey: ["device-patches", id] });
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["patch-summary"] });
    },
  });
}

export function useDeviceMetrics(
  id: string | undefined,
  metric: MetricName,
  range: RangeKey,
  enabled = true,
) {
  return useQuery({
    // Zaman penceresi (from/to) ANAHTARA GİRMEZ. `rangeWindow()` her render'da
    // Date.now() üretiyordu; anahtar sürekli değişince React Query sonsuz
    // refetch döngüsüne girip sunucuyu 429'a boğuyordu (tüm panel dahil).
    // Pencere fetch anında hesaplanır, tazelik refetchInterval ile sağlanır.
    queryKey: ["device-metrics", id, metric, range],
    queryFn: () =>
      api.devices.metrics(id as string, { metric, ...rangeWindow(range) }),
    enabled: Boolean(id) && enabled,
    placeholderData: keepPreviousData,
    refetchInterval: rangeIntervalSeconds[range] * 1000,
  });
}

export function useDeviceInventory(id: string | undefined) {
  return useQuery({
    queryKey: ["device-inventory", id],
    queryFn: () => api.devices.inventory(id as string),
    enabled: Boolean(id),
  });
}

export function useDevicePatches(
  id: string | undefined,
  query: DevicePatchQuery = {},
) {
  return useQuery({
    queryKey: ["device-patches", id, query],
    queryFn: () => api.devices.patches(id as string, query),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

/* ------------------------------------------------------------------ */
/* Patch özeti                                                         */
/* ------------------------------------------------------------------ */

export function usePatchSummary(page = 1, perPage = 50) {
  return useQuery({
    queryKey: ["patch-summary", page, perPage],
    queryFn: () => api.patches.summary({ page, perPage }),
    placeholderData: keepPreviousData,
  });
}

/* ------------------------------------------------------------------ */
/* Uyarılar                                                            */
/* ------------------------------------------------------------------ */

export function useAlerts(query: AlertListQuery) {
  return useQuery({
    queryKey: ["alerts", query],
    queryFn: () => api.alerts.list(query),
    placeholderData: keepPreviousData,
  });
}

export function useAckAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.alerts.ack(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useAlertRules() {
  return useQuery({
    queryKey: ["alert-rules"],
    queryFn: () => api.alertRules.list(),
  });
}

export function useSaveAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { rule: AlertRule; id?: string }) =>
      input.id
        ? api.alertRules.update(input.id, input.rule)
        : api.alertRules.create(input.rule),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
    },
  });
}

export function useDeleteAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.alertRules.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* SNMP hedefleri                                                      */
/* ------------------------------------------------------------------ */

export function useSnmpTargets(page = 1, perPage = 50) {
  return useQuery({
    queryKey: ["snmp-targets", page, perPage],
    queryFn: () => api.snmp.list({ page, perPage }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateSnmpTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SnmpTargetCreate) => api.snmp.create(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["snmp-targets"] });
    },
  });
}

export function useDeleteSnmpTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.snmp.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["snmp-targets"] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Kullanıcılar                                                        */
/* ------------------------------------------------------------------ */

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => api.users.list(),
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateRequest) => api.users.create(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: UserUpdateRequest }) =>
      api.users.update(input.id, input.body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.users.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Cihaz olay akışı                                                    */
/* ------------------------------------------------------------------ */

export function useDeviceEvents(
  id: string | undefined,
  query: DeviceEventQuery = {},
) {
  return useQuery({
    queryKey: ["device-events", id, query],
    queryFn: () => api.devices.events(id as string, query),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

/* ------------------------------------------------------------------ */
/* Enrollment tokenları                                                */
/* ------------------------------------------------------------------ */

export function useCreateEnrollmentToken() {
  return useMutation({
    mutationFn: (body: EnrollmentTokenRequest) => api.enrollment.createToken(body),
  });
}

/* ------------------------------------------------------------------ */
/* Filo trendi (dashboard)                                             */
/* ------------------------------------------------------------------ */

/**
 * Seçili cihaz örneklemi üzerinde metrik ortalaması üretir.
 * (Backend'de toplulaştırma ucu geldiğinde bu sorgu tek isteğe düşer.)
 */
export function useFleetTrend(
  deviceIds: string[],
  metric: MetricName,
  range: RangeKey,
) {
  const key = [...deviceIds].sort().join(",");
  return useQuery({
    // Pencere anahtara girmez (bkz. useDeviceMetrics): Date.now() her render'da
    // değişince dashboard de sonsuz refetch döngüsüne giriyordu.
    queryKey: ["fleet-trend", key, metric, range],
    queryFn: async (): Promise<ChartPoint[]> => {
      const window = rangeWindow(range);
      const series = await Promise.all(
        deviceIds.map((deviceId) =>
          api.devices.metrics(deviceId, { metric, ...window }),
        ),
      );
      return averageSeries(series);
    },
    enabled: deviceIds.length > 0,
    placeholderData: keepPreviousData,
    refetchInterval: rangeIntervalSeconds[range] * 1000,
  });
}